import { AssessmentDetail, QuestionnaireAnswer, RiskIndicatorType, RiskMetric } from '@api';
import { DropdownItemType } from '@components/ui';
import { DataCollectionInputs } from '@types';
import { action, makeObservable, observable } from 'mobx';

export const initialState = {
  modelType: null,
  performanceMetricRanked: [],
  protectedGroupsRanked: [],
  protectedGroupsFile: null,
  existingProtectedGroupDatasetId: null,
  reportType: null,
  textGenerationPersonalDataCategories: [],
  textGenerationInsightfulnessCriteria: [],
  styleCriteria: [],
  isModelLocked: false,
};

class QuestionnaireStore {
  currentStep: number = 1;

  maxStep: number = 1;

  /**
   * Async "can leave current step" guard.
   */
  leaveGuard: null | (() => Promise<boolean>) = null;

  /**
   * Tracks whether the current step is valid.
   */
  isCurrentStepValid: boolean = true;

  riskMetrics: RiskMetric[] = null;

  riskIndicators: RiskIndicatorType[] = null;

  selectedRiskMetrics: RiskMetric[] = [];

  questionnaireAnswers: QuestionnaireAnswer[] | null = null;

  questionnaireData: DataCollectionInputs = initialState;

  selectedQuestionnaireAnswersOption: DropdownItemType = null;

  latestAssessment: AssessmentDetail = null;

  constructor() {
    makeObservable(this, {
      currentStep: observable,
      maxStep: observable,
      leaveGuard: observable,
      isCurrentStepValid: observable,
      latestAssessment: observable,
      riskMetrics: observable,
      selectedRiskMetrics: observable,
      questionnaireAnswers: observable,
      questionnaireData: observable,
      selectedQuestionnaireAnswersOption: observable,
      riskIndicators: observable,
      setLeaveGuard: action.bound,
      setCurrentStepValid: action.bound,
      addQuestionnaireData: action.bound,
      resetAllModelData: action.bound,
      resetQuestionnaireAnswersAndSelectedMetrics: action.bound,
      setLatestAssessment: action.bound,
      setRiskMetrics: action.bound,
      setSelectedRiskMetrics: action.bound,
      setQuestionnaireAnswers: action.bound,
      setQuestionnaireData: action.bound,
      setCurrentStep: action.bound,
      setMaxStep: action.bound,
      setSelectedQuestionnaireAnswersOption: action.bound,
      setRiskIndicators: action.bound,
    });
  }

  setLeaveGuard(guard: null | (() => Promise<boolean>)) {
    this.leaveGuard = guard;
  }

  setCurrentStepValid(valid: boolean) {
    this.isCurrentStepValid = valid;
  }

  setSelectedRiskMetrics(metrics: RiskMetric[]) {
    this.selectedRiskMetrics = metrics;
  }

  setQuestionnaireData(data: DataCollectionInputs) {
    this.questionnaireData = data;
  }

  addQuestionnaireData(data: Partial<DataCollectionInputs>) {
    this.questionnaireData = { ...this.questionnaireData, ...data };
  }

  setCurrentStep(step: number) {
    this.currentStep = step;
  }

  setMaxStep(step: number) {
    this.maxStep = step;
  }

  setSelectedQuestionnaireAnswersOption(selected: DropdownItemType) {
    this.selectedQuestionnaireAnswersOption = selected;
  }

  setLatestAssessment(assessment: AssessmentDetail) {
    this.latestAssessment = assessment;
  }

  setQuestionnaireAnswers(answers: QuestionnaireAnswer[]) {
    this.questionnaireAnswers = answers;
  }

  setRiskMetrics(metrics: RiskMetric[]) {
    this.riskMetrics = metrics;
  }

  setRiskIndicators(indicators: RiskIndicatorType[]) {
    this.riskIndicators = indicators;
  }

  resetAllModelData() {
    this.questionnaireAnswers = null;
    this.latestAssessment = null;
    this.riskMetrics = null;
    this.selectedRiskMetrics = [];
    this.questionnaireData = null;
    this.selectedQuestionnaireAnswersOption = null;
    this.maxStep = 1;
    this.leaveGuard = null;
    this.isCurrentStepValid = true;
  }

  resetQuestionnaireAnswersAndSelectedMetrics() {
    this.latestAssessment = null;
    this.selectedRiskMetrics = [];
    this.questionnaireAnswers = null;
    this.selectedQuestionnaireAnswersOption = null;
  }
}

export const questionnaireStore = new QuestionnaireStore();
